// Feature A (#499): FL delivery-day availability by county. A runtime-editable
// rule can DISABLE Monday or Wednesday for a geographic area (a county, or a
// specific ZIP override), so FL-direct clients in that area only see the allowed
// day(s) at checkout. This module is the PURE core: types + resolver + predicates.
// No IO - the DDB layer lives in lib/aws/dynamodb/delivery-day-rules.ts.
//
// INVARIANT: allowedWeekdays is always a SUBSET of the two existing FL delivery
// weekdays {1 (Mon), 3 (Wed)}. The feature only ever REMOVES a day, never adds a
// new weekday - so every downstream cutoff / schedule / format calc stays valid
// and untouched. No rule for an area = BOTH days = byte-identical to today.
//
// FL-DIRECT ONLY: callers apply this exclusively to the FL in-state delivery
// lane. The FedEx-door / airport / own-carrier lanes have their own schedules and
// never consult these rules.

import { etWeekday, etDayFromIso, isoFromEtDay } from "./et-days";
import type { DeliveryWeekday } from "./next-deliveries";

// The two FL delivery weekdays, in canonical order (Mon, Wed).
export const FL_DELIVERY_WEEKDAYS: readonly DeliveryWeekday[] = [1, 3];

export type DeliveryDayRuleScope = "county" | "zip";

export type DeliveryDayRule = {
  // Canonical PK: "county#<Name>" or "zip#<5-digit>". Build via areaKeyFor*.
  areaKey: string;
  scope: DeliveryDayRuleScope;
  // The targeted area (county name, or the 5-digit ZIP). Denormalized for the
  // admin UI + audit; areaKey is the source of truth for lookups.
  area: string;
  // Subset of {1,3}. Empty = BOTH days disabled for the area (a full FL-delivery
  // pause) - permitted, but the admin UI flags it distinctly.
  allowedWeekdays: DeliveryWeekday[];
  note?: string;
  updatedAt: string;
  lastUpdatedBy?: string;
};

export function areaKeyForCounty(county: string): string {
  return `county#${county.trim()}`;
}

// ZIP keys use the 5-digit form (ZIP+4 is chopped) so a stored override matches
// whatever the fee resolver normalized the client's ZIP to.
export function areaKeyForZip(zip: string): string {
  return `zip#${zip.trim().split("-")[0]}`;
}

// Coerce any stored/submitted allowedWeekdays value to the canonical subset of
// {1,3}, deduped + sorted. Defends every consumer against bad or legacy data.
export function normalizeAllowedWeekdays(input: unknown): DeliveryWeekday[] {
  const set = new Set<DeliveryWeekday>();
  if (Array.isArray(input)) {
    for (const v of input) {
      if (v === 1 || v === 3) set.add(v);
    }
  }
  return ([...set].sort((a, b) => a - b)) as DeliveryWeekday[];
}

// Resolve the allowed FL delivery weekdays for a client, given their delivery ZIP
// + resolved county + the full rule set. A ZIP override wins over the county rule;
// no matching rule = both days (the default). Always returns a subset of {1,3}.
export function resolveAllowedWeekdays(input: {
  zip?: string | null;
  county?: string | null;
  rules: DeliveryDayRule[];
}): DeliveryWeekday[] {
  const { zip, county, rules } = input;
  const byKey = new Map(rules.map((r) => [r.areaKey, r]));
  if (zip) {
    const z = byKey.get(areaKeyForZip(zip));
    if (z) return normalizeAllowedWeekdays(z.allowedWeekdays);
  }
  if (county) {
    const c = byKey.get(areaKeyForCounty(county));
    if (c) return normalizeAllowedWeekdays(c.allowedWeekdays);
  }
  return [...FL_DELIVERY_WEEKDAYS];
}

// True when a full week's default {Mon,Wed} is in force (no restriction). Used to
// short-circuit predicate construction so the unrestricted path is a no-op.
export function isUnrestricted(allowed: DeliveryWeekday[]): boolean {
  const n = normalizeAllowedWeekdays(allowed);
  return n.length === FL_DELIVERY_WEEKDAYS.length;
}

// True when `weekday` (1..7, ET) is an allowed FL delivery day. Non-Mon/Wed days
// are never allowed - they were never FL delivery days to begin with.
export function isWeekdayAllowed(weekday: number, allowed: DeliveryWeekday[]): boolean {
  return (weekday === 1 || weekday === 3) && allowed.includes(weekday as DeliveryWeekday);
}

// True when an ISO (ET calendar day) is an allowed FL delivery day.
export function isIsoDayAllowed(iso: string, allowed: DeliveryWeekday[]): boolean {
  return isWeekdayAllowed(etWeekday(etDayFromIso(iso)), allowed);
}

// True when a Date (an ET-day anchor at noon UTC, as produced by the delivery
// modules) falls on an allowed FL delivery day. The anchor's UTC date IS the ET
// date by construction (see cutoffs.ts isoOf), so we read it directly.
export function isDateDayAllowed(d: Date, allowed: DeliveryWeekday[]): boolean {
  const iso = isoFromEtDay({
    year: d.getUTCFullYear(),
    month: d.getUTCMonth(),
    day: d.getUTCDate(),
  });
  return isIsoDayAllowed(iso, allowed);
}

// A Date-based "blocked" predicate that composes with the existing FL isBlocked
// hook (getNextDeliveries / nextAvailableDates / DateCards / cart auto-roll):
// an allowed-set that omits a weekday marks that weekday's dates blocked. When
// the set is the full default {1,3}, this is a no-op returning false - so the
// unrestricted path stays byte-identical to today.
export function makeAreaBlockedDate(
  allowed: DeliveryWeekday[],
): (d: Date) => boolean {
  if (isUnrestricted(allowed)) return () => false;
  return (d: Date) => !isDateDayAllowed(d, allowed);
}

// Compose the seasonal (or any existing) Date-blocked predicate with the area
// restriction. A date is blocked if EITHER blocks it. Returns the original
// predicate unchanged when unrestricted (no wrapper allocation).
export function composeBlockedDate(
  base: ((d: Date) => boolean) | undefined,
  allowed: DeliveryWeekday[],
): ((d: Date) => boolean) | undefined {
  if (isUnrestricted(allowed)) return base;
  const area = makeAreaBlockedDate(allowed);
  if (!base) return area;
  return (d: Date) => base(d) || area(d);
}
